import React from "react";
import Button from "./Button";
import Loader from "./Loader";
import SocialIcon from "./SocialIcon";

const toBoolean = (value, fallback = true) => {
  if (value == null) return fallback;
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["false", "0", "no", "off"].includes(normalized)) return false;
    if (["true", "1", "yes", "on"].includes(normalized)) return true;
  }
  return Boolean(value);
};

/**
 * ArticleCard component displays an article preview card.
 * Simplified JSX: a single container with `card_media` and `card_body`.
 * CSS dictates all layout variants; no structural branching here.
 */
const ArticleCard = ({
  showHeader = false, // retained for compatibility; no structural effect
  avatarUrl = "",
  name = "",
  role = "",
  content,
  metaRows,
  actions,
  footerMeta = "",
  title = "",
  subtitle = "",
  imageUrl = "",
  socialIconName = "",
  socialIconSrc = "",
  socialIconLabel = "",
  socialIconHref = "",
  socialIconSize = "md",
  videoIcon = "play_arrow",
  likesCount,
  hAction = "star",
  eyebrow = "",
  ctaLabel = "Learn More",
  ctaIcon = "arrow_forward",
  ctaProps = {},
  className = "",
  revealOnScroll = true,
  showImage = true,
  showCta = true,
  // Content alignment: accept same values as HeroBanner
  // e.g., "center", "top-center", "center-right", "bottom-left"
  contentAlign = "left",
  btnType = "primary",
  // Retained props for API compatibility; layout handled by CSS classes
  variant = undefined,
  imageTop = undefined,
  isLoading = false,
}) => {
  const mediaVisible = toBoolean(showImage, true);
  const ctaVisible = toBoolean(showCta, true);
  const defaultCtaProps = { primary: false };
  const resolvedCtaProps = { ...defaultCtaProps, ...ctaProps };
  const imageSrc = mediaVisible ? imageUrl || "https://placehold.co/549x219" : "";
  const revealActive = Boolean(revealOnScroll);
  const [revealed, setRevealed] = React.useState(!revealActive);
  const [imageLoaded, setImageLoaded] = React.useState(!mediaVisible);
  const rootRef = React.useRef(null);
  
  React.useEffect(() => {
    if (!mediaVisible || !imageSrc) {
      setImageLoaded(true);
      return undefined;
    }
    setImageLoaded(false);
    const img = new Image();
    img.src = imageSrc;
    img.onload = () => setImageLoaded(true);
    return undefined;
  }, [imageSrc, mediaVisible]);

  const metaList = Array.isArray(metaRows)
    ? metaRows.filter(Boolean)
    : metaRows
      ? [metaRows]
      : [];
  const actionList = Array.isArray(actions)
    ? actions.filter(Boolean)
    : actions
      ? [actions]
      : [];
  const hasSocialIcon = mediaVisible && Boolean(socialIconName || socialIconSrc);

  // Map `btnType` to Button boolean props (exclusive)
  const ctaFlags = React.useMemo(() => {
    const type = (btnType || "primary").toLowerCase();
    return {
      primary: type === "primary",
      secondary: type === "secondary",
      link: type === "link",
    };
  }, [btnType]);

  const renderActions = (
    className = "card_actions",
    keyPrefix = "article-action",
  ) => {
    if (actionList.length === 0) return null;
    return (
      <div className={className} aria-label="Article actions">
        {actionList.map((action, index) => {
          if (React.isValidElement(action)) {
            return React.cloneElement(action, {
              key: `${keyPrefix}-${index}`,
            });
          }
          if (action && typeof action === "object") {
            const { label, icon, ...buttonProps } = action;
            // sanitize handlers/links to avoid React warnings
            if (
              "onClick" in buttonProps &&
              typeof buttonProps.onClick !== "function"
            ) {
              delete buttonProps.onClick;
            }
            if ("href" in buttonProps && typeof buttonProps.href !== "string") {
              delete buttonProps.href;
            }
            const hasLabel = Boolean(label);
            return (
              <Button
                key={`${keyPrefix}-${index}`}
                label={label}
                icon={icon}
                iconOnly={!hasLabel}
                size="sm"
                as="a"
                {...buttonProps}
              />
            );
          }
          return null;
        })}
      </div>
    );
  };

  // Map variant prop to root class names so CSS controls layout
  const variantClasses = React.useMemo(() => {
    const v = String(variant || "").toLowerCase();
    const classes = [];
    if (v === "a_card") {
      classes.push("article_card_top");
    } else if (v === "compact") {
      classes.push("article_card_compact");
    }
    return classes;
  }, [variant, imageTop]);

  React.useEffect(() => {
    if (!revealActive) {
      setRevealed(true);
      return;
    }
    if (typeof window === "undefined") return;
    const reduceMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    );
    if (reduceMotion && reduceMotion.matches) {
      setRevealed(true);
      return;
    }
    const el = rootRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setRevealed(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.2 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [revealActive]);

  // Root and container classes: keep minimal structure
  const alignClass = `align-${String(contentAlign || "left")
    .toLowerCase()
    .replace(/\s+/g, "-")}`;
  const rootClasses = [
    "article_card",
    "flex",
    "flex-col",
    alignClass,
    revealActive ? "is-reveal" : "",
    revealed ? "is-revealed" : "",
    ...variantClasses,
    className,
  ]
    .filter(Boolean)
    .join(" ");
  const containerClasses = ["container", "flex", "flex-col"]
    .concat(variantClasses.includes("article_card_compact") ? ["flex-row"] : [])
    .filter(Boolean)
    .join(" ");
  const isTopVariant = variantClasses.includes("article_card_top");
  const showFooter = Boolean(ctaVisible || actionList.length > 0);

  const revealItemClass =
    revealActive && revealed ? "animate__animated animate__slideInUp" : "";
  const revealMediaClass =
    revealActive && revealed ? "animate__animated animate__fadeInUp" : "";
  const revealStyle = (delay) =>
    revealActive && revealed ? { animationDelay: `${delay}s` } : undefined;
  const ctaAs =
    resolvedCtaProps?.as || (resolvedCtaProps?.href ? "a" : "button");
  const ctaHref =
    typeof resolvedCtaProps?.href === "string" ? resolvedCtaProps.href : "#";

  const cardFooter = (
    <div
      className={["card_footer", "flex", "items-center", revealItemClass]
        .filter(Boolean)
        .join(" ")}
      style={revealStyle(0.3)}
    >
      {ctaVisible && (
        <Button
          {...ctaFlags}
          label={ctaLabel}
          as={ctaAs}
          href={ctaAs === "a" ? ctaHref : undefined}
          size="sm"
          primary={true}
          iconLeft={ctaIcon}
          {...resolvedCtaProps}
        />
      )}
      {actionList.length > 0 && (
        <div className="card_actions inline-flex items-center">
          {renderActions("card_actions", "article-action-body")}
        </div>
      )}
    </div>
  );

  const body = (
    <div className={containerClasses}>
      {/* Media area with background image */}
      {mediaVisible && (
        <div
          className={[
            "card_media",
            "flex",
            "items-start",
            "justify-end",
            revealMediaClass,
            !imageLoaded ? "animate-pulse bg-gray-200" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          style={{
            ...revealStyle(0),
            ...(imageLoaded ? {} : { backgroundImage: "none" }),
          }}
        >
          {eyebrow && <span className="card_eyebrow t-caption">{eyebrow}</span>}
          <meta itemProp="image" content={imageSrc} />
        </div>
      )}
      {hasSocialIcon && (
        <div className="card_social">
          {socialIconHref ? (
            <a
              className="card_social_link"
              href={socialIconHref}
              aria-label={socialIconLabel || socialIconName || "Social link"}
            >
              <SocialIcon
                name={socialIconName}
                src={socialIconSrc}
                size={socialIconSize}
                title={socialIconLabel}
              />
            </a>
          ) : (
            <SocialIcon
              name={socialIconName}
              src={socialIconSrc}
              size={socialIconSize}
              title={socialIconLabel}
              aria-label={socialIconLabel}
            />
          )}
        </div>
      )}

      {/* Content area */}
      <div className="card_body flex flex-col">
        {metaList.length > 0 && (
          <div
            className={["card_topline", "flex", "items-start", revealItemClass]
              .filter(Boolean)
              .join(" ")}
            style={revealStyle(0.06)}
          >
            {metaList.length > 0 && (
              <div
                className="card_meta card_meta_inline flex items-center"
                aria-label="Article meta"
              >
                {metaList.map((metaItem, index) => (
                  <p
                    key={`meta-inline-${index}`}
                    className="card_meta_item t-caption"
                  >
                    {metaItem}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}

        {title && (
          <h3
            itemProp="headline"
            className={["t-h4", revealItemClass].filter(Boolean).join(" ")}
            style={revealStyle(0.12)}
          >
            {title}
          </h3>
        )}
        {content && (
          <p
            className={["card_summary", "t-body", revealItemClass]
              .filter(Boolean)
              .join(" ")}
            style={revealStyle(0.18)}
            itemProp="description"
          >
            {content}
          </p>
        )}

        {!isTopVariant && showFooter && cardFooter}
      </div>
      {isTopVariant && showFooter && cardFooter}
    </div>
  );

  return (
    <article
      style={{
        "--card-media-image": imageLoaded ? `url(${imageSrc})` : "none",
      }}
      className={rootClasses}
      ref={rootRef}
      itemScope
      itemType="https://schema.org/Article"
    >
      {isLoading ? <Loader /> : body}
    </article>
  );
};

export default ArticleCard;
